import { isNil, isNumber } from 'lodash';
import moment from 'moment';
import { dateToAbsolute } from '../manipulation/date-manipulator.service';
/**
 * Formats a time range for use in OpenSearch/Elasticsearch queries.
 * Converts numeric timestamps to ISO strings with UTC timezone.
 * Leaves relative values (e.g., 'now-1h', 'now') as-is.
 *
 * @param from - Start time as RelativeValue (e.g., 'now-1h') or timestamp (number)
 * @param to - End time as RelativeValue (e.g., 'now') or timestamp (number)
 * @returns Formatted time range with start and end as ISO strings or relative values
 * @throws Error if parameters are invalid
 *
 * @example
 * ```ts
 * const range = formatTimeRange(1609459200000, 1609545600000);
 * //* { start: '2021-01-01T00:00:00.000Z', end: '2021-01-02T00:00:00.000Z' }
 *
 * const relativeRange = formatTimeRange('now-1h', 'now');
 * //* { start: 'now-1h', end: 'now' }
 * ```
 */
/**
 * Helper to format ISO string, inlined to avoid circular dependency with core/formatting
 */
const formatISO = (value, useUTC = true) => {
    if (useUTC) {
        return moment.utc(value).toISOString();
    }
    return moment(value).toISOString();
};
export const formatTimeRange = (from, to) => {
    const formattedDate = { start: '', end: '' };
    if (isNil(from) || isNil(to))
        return formattedDate;
    if (isNumber(from)) {
        formattedDate.start = formatISO(from, true);
    }
    else {
        formattedDate.start = from;
    }
    if (isNumber(to)) {
        formattedDate.end = formatISO(to, true);
    }
    else {
        formattedDate.end = to;
    }
    return formattedDate;
};
/**
 * Converts a time range to start and end timestamps in milliseconds.
 * Converts relative values (e.g., 'now-1h') to absolute timestamps.
 * Returns NaN for start/end if from or to is undefined/null.
 *
 * @param from - Start time as RelativeValue (e.g., 'now-1h') or timestamp (number) or undefined
 * @param to - End time as RelativeValue (e.g., 'now') or timestamp (number) or undefined
 * @returns Object with start and end timestamps in milliseconds, or NaN if undefined
 * @throws Error if resulting timestamps are invalid
 *
 * @example
 * ```ts
 * const { start, end } = getStartEnd('now-1h', 'now');
 * //* { start: 1609455600000, end: 1609459200000 }
 *
 * const { start, end } = getStartEnd(1609459200000, 1609545600000);
 * //* { start: 1609459200000, end: 1609545600000 }
 * ```
 */
export const getStartEnd = (from, to) => {
    if (isNil(from) || isNil(to)) {
        return { start: NaN, end: NaN };
    }
    const start = isNumber(from) ? from : dateToAbsolute(from) * 1000;
    const end = isNumber(to) ? to : dateToAbsolute(to) * 1000;
    return { start, end };
};
export const parseTimeRange = (timeRange) => {
    try {
        const start = typeof timeRange.start === 'string' && !timeRange.start.includes('now')
            ? moment.utc(timeRange.start).valueOf()
            : null;
        const end = typeof timeRange.end === 'string' && !timeRange.end.includes('now') ? moment.utc(timeRange.end).valueOf() : null;
        if (start === null || end === null) {
            return null;
        }
        return { start, end };
    }
    catch {
        return null;
    }
};
//# sourceMappingURL=time-range.js.map